import { useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Button,
  IconCheck,
  useDialogControllers
} from '@machobear/design-system'
import noop from 'lodash/noop'

import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useFlag from '@/hooks/use-flag'
import useRole from '@/hooks/use-role'
import useSessionComplete from '@/hooks/use-session-complete'
import { useShowLandlordErrorToast } from '@/hooks/use-show-landlord-error-toast'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { useInquiryMessage } from '@/features/Listings/contact-form/useInquiryMessage'
import { useIsListingInRecentMessages } from '@/features/Listings/hooks/recent-messages'
import {
  AUTOSEND_END,
  AUTOSEND_START,
  useMessageModalContext
} from '@/features/ListingsMap/map-list/context'
import { ViewListingButtonIcon } from '@/components/icons'

import { useTermsDialogContext } from '../terms-dialog/context'
import DSMessageModal from './ds-message-modal'
import type { Listing } from './types'
import { setListingCardDataLayer } from './utils'

type Props = {
  listing: Listing
  isCustomCompanyPage?: boolean
  loading?: boolean
  customBaseUrl?: string
  messageClickId?: string
  isCardLocked?: boolean
  featured?: boolean
  stackButtons?: boolean
  onComplete?: () => void
}

const getMessageButtonClickId = (
  isLoggedIn: boolean,
  allowApplications: boolean,
  messageClickId: string | undefined
) => {
  const userState = isLoggedIn ? 'logged' : 'listing'
  const clickId = messageClickId ?? 'message-click'
  return allowApplications
    ? `${userState}-${clickId}`
    : `${userState}-blockapp-${clickId}`
}

export const ListingCardActions = ({
  listing,
  isCustomCompanyPage = false,
  loading = false,
  messageClickId,
  isCardLocked = false,
  featured = false,
  stackButtons = false,
  onComplete = noop
}: Props) => {
  const isLoggedIn = useSessionComplete()
  const isLandlord = useRole() === 'landlord'
  const { t } = useTranslation()
  const { visible, showModal, closeModal } = useDialogControllers({
    message: false,
    login: false
  })
  const { showModalForRequiredTerms } = useTermsDialogContext()
  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showModal('login')
  })
  const showLandlordErrorToast = useShowLandlordErrorToast()
  const { autosendInquiryMessage } = useInquiryMessage(isLoggedIn)

  const {
    currentListing,
    countDownStarted,
    isAutoSendingMessage,
    broadcast,
    showAutosendMessageModal,
    setCurrentListing,
    unsetCurrentListing,
    setCountDownStarted,
    setIsAutoSendingMessage,
    showInquiryUndoToast
  } = useMessageModalContext()

  const listingId = listing.listing_id
  const allowApplications = listing.allow_applications

  const messageButtonClickId = getMessageButtonClickId(
    isLoggedIn,
    allowApplications,
    messageClickId
  )
  const recentlyMessaged = useIsListingInRecentMessages(listingId)

  const { data: defaultMoveInDateFlag } = useFlag({
    flagName: 'default_move_in_date'
  })

  const handleMessageClick = useCallback(() => {
    setListingCardDataLayer({
      listing,
      featured,
      eventId: messageButtonClickId
    })

    if (isLandlord) {
      return showLandlordErrorToast()
    }
    isCardLocked
      ? ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', () =>
            showModal('message')()
          )
        )
      : showModal('message')()
  }, [
    listing,
    featured,
    messageButtonClickId,
    isLandlord,
    isCardLocked,
    ensureLoggedIn,
    showModal,
    showLandlordErrorToast,
    showModalForRequiredTerms
  ])

  const autosendInquiryMessageOnTimeout = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (isLandlord) {
        return showLandlordErrorToast()
      }

      if (isCardLocked) {
        ensureLoggedIn(() =>
          showModalForRequiredTerms('trreb_agreements', () =>
            showModal('message')()
          )
        )
        return
      }

      if (!isLoggedIn) {
        if (
          !sessionStorage.getItem('X-Temp-Access-Key') ||
          !localStorage.getItem('inquiry_form_values')
        ) {
          handleMessageClick()
          return
        }
      }

      if (isLoggedIn && !defaultMoveInDateFlag) {
        handleMessageClick()
        return
      }

      setCurrentListing(listing)
      setCountDownStarted(true)

      broadcast({
        type: AUTOSEND_START,
        payload: {
          listing
        }
      })

      const removeToast = showInquiryUndoToast({
        timeout: 2.5,
        onUndo: () => {
          unsetCurrentListing()
          showAutosendMessageModal(listing, isCardLocked, isLandlord)
          removeToast()
          setCountDownStarted(false)
          broadcast({
            type: AUTOSEND_END,
            payload: {
              listing
            }
          })
        },
        onTimeout: () => {
          setCountDownStarted(false)
          setIsAutoSendingMessage(true)
          autosendInquiryMessage({
            listing,
            XTempAccessKey: isLoggedIn
              ? null
              : sessionStorage.getItem('X-Temp-Access-Key'),
            onTokenExpired: () => {
              unsetCurrentListing()
              setIsAutoSendingMessage(false)
              removeToast()
              showAutosendMessageModal(listing, isCardLocked, isLandlord)
              broadcast({
                type: AUTOSEND_END,
                payload: {
                  listing
                }
              })
            },
            onFormValuesInvalid: () => {
              unsetCurrentListing()
              setIsAutoSendingMessage(false)
              removeToast()
              showAutosendMessageModal(listing, isCardLocked, isLandlord)
              broadcast({
                type: AUTOSEND_END,
                payload: {
                  listing
                }
              })
            },
            onComplete: () => {
              setListingCardDataLayer({
                listing,
                featured,
                eventId: messageButtonClickId
              })
              unsetCurrentListing()
              removeToast()
              setIsAutoSendingMessage(false)
              broadcast({
                type: AUTOSEND_END,
                payload: {
                  listing
                }
              })
            }
          })
        }
      })
    },
    [
      isLandlord,
      isCardLocked,
      isLoggedIn,
      defaultMoveInDateFlag,
      setCurrentListing,
      listing,
      setCountDownStarted,
      broadcast,
      showInquiryUndoToast,
      showLandlordErrorToast,
      ensureLoggedIn,
      showModalForRequiredTerms,
      showModal,
      handleMessageClick,
      unsetCurrentListing,
      showAutosendMessageModal,
      setIsAutoSendingMessage,
      autosendInquiryMessage,
      featured,
      messageButtonClickId
    ]
  )

  let hasCheckmark = !featured && recentlyMessaged

  if (currentListing?.listing_id === listing.listing_id) {
    hasCheckmark = !countDownStarted && !isAutoSendingMessage && hasCheckmark
  }

  return (
    <Box
      display="flex"
      gap="xxsmall"
      flexDirection={stackButtons ? 'column' : 'row'}
      onClick={e => e.stopPropagation()}
    >
      {!isCustomCompanyPage ? (
        <>
          <Button
            onClick={autosendInquiryMessageOnTimeout}
            id={messageButtonClickId}
            disabled={
              loading ||
              recentlyMessaged ||
              countDownStarted ||
              isAutoSendingMessage
            }
            icon={hasCheckmark ? <IconCheck /> : undefined}
          >
            {currentListing?.listing_id === listing.listing_id &&
            (countDownStarted || isAutoSendingMessage)
              ? t('Sending...')
              : recentlyMessaged
              ? t('b.listing.view.message_sent.button', 'Message Sent')
              : t('b.listing.card.send_message.button', 'Send Message')}
          </Button>
        </>
      ) : (
        <Button
          onClick={() => {}}
          id={`${
            isLoggedIn ? 'logged' : 'listing'
          }-city-page-apply-click-${listingId}`}
          disabled={loading}
          icon={<ViewListingButtonIcon height={8} width={8} />}
          iconPosition="trailing"
        >
          {t('b.listing.view.view_listing.button', 'View listing')}
        </Button>
      )}
      <DSMessageModal
        visible={visible.message}
        onClose={closeModal('message')}
        onComplete={() => {
          onComplete()
        }}
        listing={listing}
      />
      <DSLoginModal
        mode="login"
        eventId={'web-signup-submit-savebutton'}
        visible={visible.login}
        onClose={closeModal('login')}
        t={t}
        listingId={parseInt(listingId)}
      />
    </Box>
  )
}
